import { List, ListItem, ListItemText, Menu, TextField } from '@mui/material';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (truck: PlanningsTruckEntity) => void;
  trucks: PlanningsTruckEntity[];
  /** 既に他 row で利用中の truck id 群 (= 選択肢から除外) */
  excludeTruckIds: ReadonlySet<number>;
  /** 現在この row が指している truck id (= 除外対象から外す) */
  currentTruckId: number | null;
};

const TruckSelectMenu: FC<Props> = memo(({ anchorEl, onClose, onSelect, trucks, excludeTruckIds, currentTruckId }) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(false);
  const [currentRow, setCurrentRow] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!anchorEl) {
      setInput('');
      setQuery('');
    }
  }, [anchorEl]);
  useEffect(() => { if (!composing) setQuery(input); }, [composing, input]);

  /**
   * 開いたら選択中の行まで送る。一覧は数百件あり先頭から始まるので、いま割り当たって
   * いる車両が画面外のままだった（ドライバー選択と同じ扱いに揃える。指摘 2026/08/19）。
   *
   * Why not ref + マウント時の useEffect: Menu の中身は Modal が後のコミットで描くので、
   * この階層の effect が走る時点では ref が null のまま（DriverSelectMenu と同じ）。
   */
  useEffect(() => {
    if (!anchorEl || !currentRow) return;
    currentRow.scrollIntoView({ block: 'nearest' });
  }, [anchorEl, currentRow]);

  const handleSelect = useCallback((truck: PlanningsTruckEntity) => {
    ReactGA.event('select', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: 'トラック選択',
      label: truck.licensePlateValue ?? '',
    });
    onSelect(truck);
    onClose();
  }, [onSelect, onClose]);

  const filtered = trucks.filter((t) => {
    if (t.temporary) return false;
    if (t.id !== currentTruckId && excludeTruckIds.has(t.id)) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (t.licensePlateValue ?? '').toLowerCase().includes(q)
      || (t.truckNumber ?? '').toLowerCase().includes(q)
      || (t.partnerCompanyName ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      sx={{ maxHeight: 500 }}
      slotProps={{ paper: { sx: { p: 0, m: 0 } } }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="検索"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onCompositionStart={() => setComposing(true)}
        onCompositionEnd={() => setComposing(false)}
      />
      <List sx={{ p: 0, m: 0, minWidth: 240 }}>
        {filtered.length === 0 && (
          <ListItem sx={{ fontSize: 12, color: 'text.secondary' }}>
            <ListItemText primary="選択可能な車両がありません" primaryTypographyProps={{ fontSize: 12 }} />
          </ListItem>
        )}
        {filtered.map((t) => {
          const isCurrent = t.id === currentTruckId;
          return (
            <ListItem
              key={`truck-${t.id}`}
              component="div"
              ref={isCurrent ? setCurrentRow : undefined}
              // 選択中の行だけ地を変える。印が無いと、名前が似た車両の並びから
              // いまどれが割り当たっているのか読み取れない
              aria-current={isCurrent ? true : undefined}
              onClick={() => handleSelect(t)}
              sx={{
                cursor: 'pointer',
                ...(isCurrent && { bgcolor: 'hsl(var(--secondary))' }),
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemText
                primary={t.licensePlateValue}
                secondary={[t.truckNumber, t.partnerCompanyName].filter(Boolean).join(' / ')}
                primaryTypographyProps={{ fontSize: 14, fontWeight: isCurrent ? 600 : undefined }}
                secondaryTypographyProps={{ fontSize: 12 }}
              />
            </ListItem>
          );
        })}
      </List>
    </Menu>
  );
});

export default TruckSelectMenu;
